// The signed-in user's Outlook connection: their account, their Outlook
// calendars and the role each has, and the actions the microsoft edge
// function takes for them (connect, sync, import To Do, disconnect). What
// every calendar source shares (the days, per-event overrides) is in
// calendar.ts. Tokens never reach the browser.

import type { CalendarRole } from '@cerebro-diem/core';
import { supabase } from './supabase';
import { invokeEdgeFunction } from './edgeFunctions';
import { isRecord } from './errors';

export interface OutlookConnection {
  account_email: string | null;
  needs_reconnect: boolean;
  last_error: string | null;
  connected_at: string;
  last_sync_at: string | null;
  tasks_imported_at: string | null;
}

export interface OutlookCalendar {
  id: string;
  name: string;
  color: string | null;
  is_default: boolean;
  role: CalendarRole;
}

export interface TaskImportResult {
  filed: number;
  inbox: number;
  already_imported: number;
  reminders_not_set: number;
}

/** Outlook's queries live under the calendar root, so a calendar invalidation refreshes them too. */
export const outlookKeys = {
  all: ['calendar', 'outlook'] as const,
  connection: ['calendar', 'outlook', 'connection'] as const,
  calendars: ['calendar', 'outlook', 'calendars'] as const,
};

/** The user's connection, or null when they have not connected. */
export async function outlookConnection(): Promise<OutlookConnection | null> {
  const { data, error } = await supabase
    .from('microsoft_connections')
    .select('account_email, needs_reconnect, last_error, connected_at, last_sync_at, tasks_imported_at')
    .maybeSingle();
  if (error) throw error;
  return (data ?? null) as OutlookConnection | null;
}

export async function outlookCalendars(): Promise<OutlookCalendar[]> {
  const { data, error } = await supabase.from('calendars').select('id, name, color, is_default, role').eq('source', 'microsoft').order('name');
  if (error) throw error;
  return (data ?? []) as OutlookCalendar[];
}

const MICROSOFT_SIGN_IN = 'https://login.microsoftonline.com/';

/** Ask the server for Microsoft's consent page and go there; Microsoft sends the user back to Integrations. */
export async function startOutlookConnect(): Promise<void> {
  const result = await invokeEdgeFunction('microsoft', { action: 'oauth_start' });
  const url = isRecord(result) ? result.url : null;
  if (typeof url !== 'string' || !url.startsWith(MICROSOFT_SIGN_IN)) {
    throw new Error('The server did not return a Microsoft sign-in address');
  }
  window.location.assign(url);
}

export async function syncOutlook(): Promise<{ calendars: number; events: number }> {
  const result = await invokeEdgeFunction('microsoft', { action: 'sync' });
  if (!isRecord(result) || typeof result.calendars !== 'number' || typeof result.events !== 'number') {
    throw new Error('The server answered the sync with something unexpected');
  }
  return { calendars: result.calendars, events: result.events };
}

export async function importOutlookTasks(): Promise<TaskImportResult> {
  const result = await invokeEdgeFunction('microsoft', { action: 'import_tasks' });
  const keys = ['filed', 'inbox', 'already_imported', 'reminders_not_set'] as const;
  if (!isRecord(result) || !keys.every((k) => typeof result[k] === 'number')) {
    throw new Error('The server answered the import with something unexpected');
  }
  return result as unknown as TaskImportResult;
}

export async function disconnectOutlook(): Promise<void> {
  await invokeEdgeFunction('microsoft', { action: 'disconnect' });
}

/** "Imported 21: 3 filed into their Project or Area, 18 in your Inbox to file. 2 were imported before." */
export function importSummary(result: TaskImportResult): string {
  const imported = result.filed + result.inbox;
  const parts: string[] = [];
  if (imported === 0) {
    parts.push('No new tasks to import.');
  } else {
    const where = [
      result.filed > 0 ? `${result.filed} filed into their Project or Area` : null,
      result.inbox > 0 ? `${result.inbox} in your Inbox to file` : null,
    ].filter(Boolean);
    parts.push(`Imported ${imported}: ${where.join(', ')}.`);
  }
  if (result.already_imported > 0) {
    parts.push(`${result.already_imported} ${result.already_imported === 1 ? 'was' : 'were'} imported before and left as they are.`);
  }
  if (result.reminders_not_set > 0) {
    parts.push(`${result.reminders_not_set} reminder${result.reminders_not_set === 1 ? '' : 's'} could not be set (set your time zone in Settings, or clear a reminder to make room).`);
  }
  return parts.join(' ');
}
